import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DoorOpen } from 'lucide-react'
import { fetchDay, toMinutes } from '../api/calendar'
import { fetchBookings, fetchHolds, roomAvailability } from '../api/bookings'
import { moveOccurrenceRoom } from '../api/classAdmin'
import type { ConflictRow } from './ConflictsView'
import type { ConflictSide } from '../api/bookings'

/**
 * Moving a class out of a room it is clashing in (D-569).
 *
 * <b>The one fix this screen can make.</b> A dancer clash is an enrolment and
 * Jackrabbit owns those; a teacher clash is a judgement about who takes what.
 * A room clash is the studio's own, and the studio owns where a class meets on
 * a given night — so this is where the conflicts page stops being a list of
 * things to think about.
 *
 * <b>Only a class side moves.</b> A booking is a reservation with its own form
 * (D-80), and editing one from here would be a second way to do a thing that
 * already has one. Where both sides are bookings the panel says so rather than
 * offering nothing.
 *
 * <b>This night, or the rest of them</b> — the shape the owner asked for:
 * <i>"like editing a meeting in Teams where it lets you update the one
 * instance or the whole series."</i> One night is usually the wrong answer
 * here, because the pairing recurs every week both things run (D-150), so the
 * consequence of each is said beside it rather than left to be discovered.
 */
export default function MoveRoomPanel({ row }: { row: ConflictRow }) {
  const on = row.dates[0]
  const movable = [row.firstSide, row.secondSide].filter((s) => s.kind === 'Class')

  const [moving, setMoving] = useState<ConflictSide | null>(movable[0] ?? null)

  /*
   * <b>Derived from what the two sides are, not from how many dates are on
   * the row</b> (D-569).
   *
   * The first cut defaulted to the series when `row.dates.length > 1`, which
   * on the real screen is almost never: the list fetches one week, and a class
   * is one weekday at one time (D-88), so a class-vs-class clash contributes
   * exactly one date. The default was therefore always "just this date" — the
   * option whose own copy says the clash comes back — and the test that
   * claimed otherwise hand-fed two dates the screen cannot produce.
   *
   * What actually says whether it recurs is the pair. Two classes both meet
   * every week, so their clash does too. A booking is a single reservation, so
   * a class clashing with one private is a one-night problem and moving the
   * whole season for it would be the larger mistake.
   */
  const [rest, setRest] = useState(recurs(row))

  const queryClient = useQueryClient()

  /*
   * The studio's whole day, which is what `roomAvailability` reads — the same
   * pair of requests the booking form makes, because "is this room free" is
   * one question and two answers to it would drift (D-396 records the last
   * time they did).
   */
  const day = useQuery({
    queryKey: ['calendar', 'day', on, { wholeStudio: true }],
    queryFn: () => fetchDay(on, { wholeStudio: true }),
  })

  const bookings = useQuery({
    queryKey: ['bookings', on, on],
    queryFn: () => fetchBookings(on, on),
  })

  // Weekend holds take their rooms (D-710). No teacher is exempted here, so a
  // teacher's own class shows her held room as taken even though the server
  // would allow it - the safe direction, and a rare case on a weekend.
  const holds = useQuery({
    queryKey: ['bookings', 'holds', on],
    queryFn: () => fetchHolds(on, on),
  })

  const move = useMutation({
    mutationFn: (roomId: string) =>
      moveOccurrenceRoom(moving!.sourceId, moving!.id, { roomId, rest }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
    },
  })

  if (movable.length === 0) {
    return (
      <p className="mt-3 rounded-lg bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2">
        Both of these are room bookings, which are changed on the booking itself.
      </p>
    )
  }

  if (!moving) return null

  const options = roomAvailability(
    day.data?.rooms ?? [],
    bookings.data ?? [],
    toMinutes(moving.start),
    toMinutes(moving.end),
    undefined,
    holds.data ?? [],
  )

  /*
   * The room it is in is not an option: it is the clash. Said by omission
   * rather than by a struck-through row, because a control offering the
   * problem as a solution is one somebody eventually clicks.
   */
  const elsewhere = options.filter((r) => r.roomId !== moving.roomId)

  return (
    <div className="mt-3 rounded-xl border border-line bg-surface-2 px-3 py-3">
      <p className="flex items-center gap-1.5 text-[11px] font-bold tracking-[0.09em] text-ink-3 uppercase">
        <DoorOpen size={12} strokeWidth={2.5} />
        Move one of them
      </p>

      {movable.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {movable.map((side) => (
            <button
              key={side.id}
              type="button"
              onClick={() => setMoving(side)}
              className={`rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold ${
                side.id === moving.id
                  ? 'border-accent bg-accent-soft text-accent-2'
                  : 'border-line-2 text-ink-2'
              }`}
            >
              {side.label}
            </button>
          ))}
        </div>
      )}

      <fieldset className="mt-2.5">
        <legend className="text-[12px] font-semibold text-ink-3">How often</legend>

        {/*
          The consequence beside each, not after the fact. One night is
          usually the wrong answer on this screen — the pairing recurs every
          week both things run (D-150) — and nothing else on the panel would
          tell her that until the row came back next week.
        */}
        <div className="mt-1.5 flex flex-col gap-1.5">
          <Scope
            on={!rest}
            onPick={() => setRest(false)}
            label="Just this date"
            why="The other nights stay where they are, so this comes back."
          />
          <Scope
            on={rest}
            onPick={() => setRest(true)}
            label="This date and the rest of the season"
            why="Held by the app for every remaining night. Jackrabbit still says the old room."
          />
        </div>
      </fieldset>

      <p className="mt-2.5 text-[12px] font-semibold text-ink-3">Move it to</p>

      {day.isPending || bookings.isPending ? (
        <p className="mt-1 text-[12px] text-ink-3">Looking at the day...</p>
      ) : day.isError || bookings.isError ? (
        // Which request failed, never a bare "no rooms" — an empty list and a
        // refused one look identical and mean opposite things (D-209).
        <p role="alert" className="mt-1 text-[12px] text-crit">
          The day could not be loaded, so there is nothing to say about free rooms.
        </p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {elsewhere.map((room) => (
            <button
              key={room.roomId}
              type="button"
              disabled={!room.isFree || move.isPending}
              onClick={() => move.mutate(room.roomId)}
              title={room.conflictLabel ?? undefined}
              className="rounded-lg border border-line-2 bg-surface px-2.5 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-accent hover:text-accent disabled:opacity-40 disabled:hover:border-line-2 disabled:hover:text-ink-2"
            >
              {room.name}
            </button>
          ))}

          {elsewhere.every((r) => !r.isFree) && (
            <p className="text-[12px] text-ink-3">
              Nothing else is free at that time.
            </p>
          )}
        </div>
      )}

      {move.isError && (
        <p role="alert" className="mt-2 text-[12px] text-crit">
          {(move.error as Error).message}
        </p>
      )}

      {move.isSuccess && (
        <p role="status" className="mt-2 text-[12px] text-good">
          Moved. Families subscribed to a calendar can take a day to see it.
        </p>
      )}
    </div>
  )
}

/**
 * Whether this clash comes back next week (D-569).
 *
 * <b>A property of the pair, not of the dates on the row.</b> A class meets on
 * one weekday at one time (D-88) and the conflicts list covers one week, so
 * two classes clashing show one date and clash again every week they both run.
 * A booking is a single reservation, so a class against one private is a
 * one-night problem — and moving a season for it would be the larger mistake.
 */
export function recurs(row: ConflictRow): boolean {
  return row.firstSide.kind === 'Class' && row.secondSide.kind === 'Class'
}

function Scope({
  on,
  onPick,
  label,
  why,
}: {
  on: boolean
  onPick: () => void
  label: string
  why: string
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={`rounded-lg border px-2.5 py-2 text-left ${
        on ? 'border-accent bg-accent-soft' : 'border-line'
      }`}
    >
      <span className="flex items-center gap-2 text-[12.5px] font-semibold">
        <span
          aria-hidden="true"
          className={`size-3.5 shrink-0 rounded-full border-2 ${
            on ? 'border-accent bg-accent' : 'border-line-2'
          }`}
        />
        {label}
      </span>
      <span className="mt-0.5 block pl-[22px] text-[12px] text-ink-3">{why}</span>
    </button>
  )
}
